import { useState, useMemo, useEffect } from 'react'
import {
  useUpdateMany,
  useRefresh,
  useNotify,
  useGetIdentity,
  useGetOne,
  useUnselectAll,
  useGetList,
  Button,
  useListContext,
  useTheme,
} from 'react-admin'
import {
  Box,
  Radio,
  Typography,
  RadioGroup,
  InputAdornment,
  FormControlLabel,
  CircularProgress,
  Button as MuiButton,
  TextField as MuiTextField,
  useTheme as useMuiTheme,
} from '@mui/material'

import DOMPurify from 'dompurify'

import { isUUID } from '../../common/isUUID'
import sendMail from '../../common/SendMail'
import { getRole } from '../../common/roles'
import { supabaseClient } from '../../supabase'
import { CommonDialog } from '../../common/Dialog'
import logAssignation from '../../common/logAssignation'
import { addNewLeadComment } from '../../common/addNewComment'
import createNotification from '../../common/createNotification'
import { getDialogSx, getInputSx } from '../../Design/CustomInput'
import { confirmationDialog } from '../../common/createConfirmation'

import SearchIcon from '@mui/icons-material/Search'
import HowToRegIcon from '@mui/icons-material/HowToReg'

const LeadAssignationContent = ({ handleBulkAction, isAuthorized, roleAdmin, isAdmin, identity, leads, currentPubeurId, currentPubeurName, leadsWithPubeurCount }) => {
  const muiTheme = useMuiTheme()
  const isDark = muiTheme.palette.mode === 'dark'
  const inputSx = getInputSx(isDark)
  const isSingle = leads?.length === 1

  const [selectedPubeur, setSelectedPubeur] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [isMomentary, setIsMomentary] = useState(false)
  const [momentaryDate, setMomentaryDate] = useState('')
  const [isAssigning, setIsAssigning] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)

  const today = useMemo(() => new Date().toISOString().split('T')[0], [])
  const isDateValid = !isMomentary || (momentaryDate && momentaryDate >= today)

  const filter = isAdmin
    ? { 'enabled': true }
    : {
        'enabled': true,
        'center_id': identity?.center?.id,
        ...(roleAdmin.length > 0 && { 'role_id@neq': [roleAdmin[0].id] })
      }

  const { data: profiles = [], isLoading: profilesLoading } = useGetList('profiles', {
    pagination: { page: 1, perPage: 9999 },
    sort: { field: 'last_name', order: 'ASC' },
    filter,
  })

  const filteredProfiles = useMemo(() => {
    if (!searchText.trim()) return profiles
    const search = searchText.toLowerCase().trim()
    return profiles.filter(p =>
      `${p.first_name} ${p.last_name}`.toLowerCase().includes(search)
      // add email
    )
  }, [profiles, searchText])

  const selectedProfile = profiles.find(p => p.id === selectedPubeur)

  const handleAssign = async () => {
    if (!selectedProfile) return
    if (isMomentary && !isDateValid) return
    setIsAssigning(true)
    await handleBulkAction(selectedProfile, isMomentary, isMomentary ? momentaryDate : null)
    setIsAssigning(false)
  }

  return (
    <Box>
      {isSingle && currentPubeurName && (
        <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary', fontSize: '0.85rem' }}>
          Pubeur actuel : <strong>{currentPubeurName}</strong>
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <MuiTextField
          placeholder="Rechercher un pubeur..."
          variant="outlined"
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ ...inputSx, flex: '0 0 70%' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: '1.1rem', color: '#999' }} />
              </InputAdornment>
            ),
          }}
        />

        {isAuthorized && (
          <MuiButton
            variant="contained"
            color="warning"
            size="small"
            disabled={isRemoving}
            sx={{ flex: 1, whiteSpace: 'nowrap' }}
            onClick={async () => {
              setIsRemoving(true)
              await handleBulkAction(null, false, null)
              setIsRemoving(false)
            }}
          >
            Désassigner
          </MuiButton>
        )}
      </Box>

      {profilesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <RadioGroup
          value={selectedPubeur || ''}
          onChange={(e) => setSelectedPubeur(e.target.value)}
        >
          <Box sx={{
            maxHeight: '300px',
            overflowY: 'auto',
            border: `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
            borderRadius: '8px',
          }}>
            {filteredProfiles.length === 0 ? (
              <Typography variant="body2" sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
                Aucun pubeur trouvé
              </Typography>
            ) : (
              filteredProfiles.map((profile) => {
                const isCurrentPubeur = isSingle && profile.id === currentPubeurId
                return (
                  <FormControlLabel
                    key={profile.id}
                    value={profile.id}
                    disabled={isCurrentPubeur}
                    control={<Radio size="small" />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          {profile.first_name} {profile.last_name}
                        </Typography>

                        {isCurrentPubeur && (
                          <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                            Déjà attribué
                          </Typography>
                        )}
                      </Box>
                    }
                    sx={{
                      mx: 0,
                      px: 2,
                      py: 0.5,
                      width: '100%',
                      borderBottom: `1px solid ${isDark ? '#222' : '#f0f0f0'}`,
                      '&:last-child': { borderBottom: 'none' },
                      '&:hover': {
                        backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                      },
                    }}
                  />
                )
              })
            )}
          </Box>
        </RadioGroup>
      )}

      {leadsWithPubeurCount > 0 && (
        <Typography variant="body2" sx={{ mt: 1.5, color: 'warning.main', fontSize: '0.75rem' }}>
          {leadsWithPubeurCount} contact{leadsWithPubeurCount > 1 ? 's' : ''}{' '}
          {leadsWithPubeurCount > 1 ? 'ont' : 'a'} déjà un pubeur.{' '}
          {leadsWithPubeurCount > 1 ? 'Leur' : 'Son'} attribution sera remplacée.
        </Typography>
      )}

      {isAuthorized && (
        <Box sx={{
          mt: 2,
          pt: 2,
          borderTop: `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
        }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
            Durée
          </Typography>

          <RadioGroup
            value={isMomentary ? 'temporary' : 'permanent'}
            onChange={(e) => setIsMomentary(e.target.value === 'temporary')}
          >
            <FormControlLabel
              value="permanent"
              control={<Radio size="small" />}
              label={<Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Permanente</Typography>}
            />

            <FormControlLabel
              value="temporary"
              control={<Radio size="small" />}
              label={<Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Temporaire, jusqu'au</Typography>}
            />
          </RadioGroup>

          {isMomentary && (
            <Box sx={{ pl: 4, mt: 1 }}>
              <input
                type="date"
                value={momentaryDate}
                min={today}
                style={{
                  fontSize: '0.85rem',
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${isDark ? '#444' : '#ccc'}`,
                  backgroundColor: isDark ? '#1e1e1e' : '#fff',
                  color: isDark ? '#ddd' : '#333',
                  boxSizing: 'border-box',
                }}
                onChange={(e) => setMomentaryDate(e.target.value)}
              />

              <Typography variant="body2" sx={{ mt: 0.5 }}>
                inclus
              </Typography>

              <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary', display: 'block' }}>
                À l'échéance, chaque contact retrouve son pubeur précédent.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          label={selectedProfile
            ? `Attribuer à ${selectedProfile.first_name} ${selectedProfile.last_name}`
            : 'Attribuer'
          }
          variant="contained"
          color="primary"
          loading={isAssigning}
          disabled={!selectedPubeur || (isMomentary && !isDateValid)}
          onClick={handleAssign}
        />
      </Box>
    </Box>
  )
}

export const PubeurAssignation = ({ defaultOpen = false, onCloseCallback, leadIds: leadIdsProp, initialPubeurId, initialPubeurName }) => {
  const refresh = useRefresh()
  const notify = useNotify()
  const unselectAll = useUnselectAll('leads')
  const [updateMany] = useUpdateMany()
  const { data: identity } = useGetIdentity()
  const isAdmin = identity?.role?.grade === getRole('admin')
  const isAuthorized = identity?.role?.grade >= getRole('sdr')
  const { selectedIds, data: leadsData = [] } = useListContext()
  const leads = leadIdsProp ?? selectedIds
  const [theme] = useTheme()
  const isDark = theme === 'dark'

  const [openAssignation, setOpenAssignation] = useState(defaultOpen)
  const [leadsWithPubeurCount, setLeadsWithPubeurCount] = useState(0)

  const { data: rolesData = [], isLoading: rolesLoading } = useGetList('roles', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'id', order: 'ASC' }
  })
  const roleAdmin = rolesData.filter(role => role.reference === 'admin')

  const currentPubeurId = useMemo(() => {
    if (leads?.length !== 1) return null
    if (initialPubeurId !== undefined) return initialPubeurId
    const lead = leadsData.find(l => l.id === leads[0])
    return lead?.assigned_pubeur || null
  }, [leads, leadsData, initialPubeurId])

  const { data: pubeurProfileData } = useGetOne(
    'profiles',
    { id: currentPubeurId },
    { enabled: !!currentPubeurId && initialPubeurName === undefined }
  )

  const currentPubeurName = useMemo(() => {
    if (leads?.length !== 1) return null
    if (initialPubeurName !== undefined) return initialPubeurName
    return pubeurProfileData?.full_name || null
  }, [leads, initialPubeurName, pubeurProfileData])

  useEffect(() => {
    if (!openAssignation || leads?.length <= 1) {
      setLeadsWithPubeurCount(0)
      return
    }

    let cancelled = false
    const validIds = leads.filter(id => isUUID(id))
    if (validIds.length === 0) return

    supabaseClient
      .from('leads')
      .select('id', { count: 'exact', head: true })
      .in('id', validIds)
      .not('assigned_pubeur', 'is', null)
      .then(({ count }) => {
        if (!cancelled) setLeadsWithPubeurCount(count || 0)
      })

    return () => { cancelled = true }
  }, [leads, openAssignation])

  const handleClose = (e) => {
    if (e) e.stopPropagation()
    setOpenAssignation(false)
    if (onCloseCallback) onCloseCallback(e)
  }

  const performAssignation = async (chunk, pubeurInfo, isMomentary = false, dateOfMomentaryAssignation = null) => {
    try {
      // Filter out invalid UUIDs upfront
      const validLeadIds = chunk.filter(id => isUUID(id))

      if (validLeadIds.length === 0) {
        notify('Aucun ID de contact valide trouvé', { type: 'warning' })
        return
      }

      if (validLeadIds.length < chunk.length) {
        const invalidIds = chunk.filter(id => !isUUID(id))
        await supabaseClient
          .from('suivi_bilans')
          .insert({
            comment: `Invalid lead IDs in PubeurAssignation: ${invalidIds.join(', ')}`,
            values: JSON.stringify({ invalid_ids: invalidIds })
          })
      }

      // Single batch query to get all lead info
      const { data: leadsInfo, error: fetchError } = await supabaseClient
        .from('leads')
        .select('id, first_name, last_name, assigned_pubeur')
        .in('id', validLeadIds)

      if (fetchError) {
        console.error('Batch fetch error:', fetchError)
        await supabaseClient
          .from('suivi_bilans')
          .insert({
            comment: `Batch fetch error in PubeurAssignation: ${fetchError.message}`,
            values: JSON.stringify({
              lead_ids: validLeadIds,
              error: fetchError.message,
              batch_size: validLeadIds.length
            })
          })
        return
      }

      // Find any missing leads
      const foundIds = leadsInfo.map(lead => lead.id)
      const missingIds = validLeadIds.filter(id => !foundIds.includes(id))

      if (missingIds.length > 0) {
        console.warn('Missing leads:', missingIds)
        await supabaseClient
          .from('suivi_bilans')
          .insert({
            comment: `Missing leads in batch assignation: ${missingIds.join(', ')}`,
            values: JSON.stringify({
              missing_lead_ids: missingIds,
              attempted_count: validLeadIds.length,
              found_count: foundIds.length
            })
          })
      }

      // Continue with only found leads
      const finalLeadIds = foundIds

      if (finalLeadIds.length === 0) {
        notify('Aucun contact trouvé pour l\'assignation', { type: 'warning' })
        return
      }

      // Perform the database update first
      let updateSuccess = false
      let updatedCount = 0

      if (identity.role.grade === 20) {
        // Use RPC function to bypass RLS
        const { data, error } = await supabaseClient
          .rpc('rpc_assign_leads_to_pubeur', {
            p_lead_ids: finalLeadIds,
            p_pubeur_id: pubeurInfo?.id ?? null
          })

        if (error || (data && !data.success)) {
          console.error('Error updating lead assigned pubeur', error || data?.error)
          notify('Une erreur est survenue', { type: 'error' })
          return
        }

        updateSuccess = true
        updatedCount = data?.updated_count || 0
      } else {
        // Use updateMany for non-grade-20 users
        await new Promise((resolve, reject) => {
          updateMany(
            'leads',
            {
              ids: finalLeadIds,
              data: { assigned_pubeur: pubeurInfo?.id ?? null }
            },
            {
              onSuccess: () => {
                updateSuccess = true
                updatedCount = finalLeadIds.length
                resolve()
              },
              onError: (error) => {
                notify(`Erreur affectation pubeur : ${error.message}`, { type: 'error' })
                reject(error)
              },
            }
          )
        })
      }

      // Only proceed with post-update actions if the update was successful
      if (!updateSuccess) {
        return
      }

      // Now handle post-update actions (logs, notifications, comments)
      if (pubeurInfo?.id) {
        // Assignment logic
        const validLeadsInfo = leadsInfo.filter(info => finalLeadIds.includes(info.id))
        const allNames = validLeadsInfo
          .map(info => DOMPurify.sanitize(`${info.first_name || ''} ${info.last_name || ''}`.trim(), { ALLOWED_TAGS: [] }))
          .filter(name => name)

        if (pubeurInfo && allNames.length > 0) {
          // Send notification email
          let messageHTML = `
            <h3>Information FormaContacts</h3>
            <h4>Un ou plusieurs contacts vous ont été assignés aujourd'hui :</h4>
            <ul>`
          messageHTML += allNames.map(leadName => `<li>${leadName}</li>`).join('')
          messageHTML += `
            </ul>
            <p><a href="${process.env.REACT_APP_WEBSITE_URL}/leads">Cliquez ici pour vous rendre sur le CRM</a></p>
            <p><i>Ceci est un message automatique depuis le CRM Forma Contacts, merci de ne pas y répondre.</i></p>
          `

          const messageTEXT = `Un ou plusieurs contacts vous ont été attribués aujourd'hui.\n\nCeci est un message automatique, merci de ne pas y répondre.`

          sendMail({
            identity: pubeurInfo,
            subject: `Nouveaux contacts assignés`,
            messageHTML: messageHTML,
            messageTEXT: messageTEXT,
            sendNow: true
          })

          const listeOfLeads = allNames.join(', ')

          // Batch create comments
          const commentPromises = finalLeadIds.map(lead_id =>
            addNewLeadComment(
              lead_id,
              identity,
              null,
              `*AUTO* Assignation du contact à ${pubeurInfo.first_name} ${pubeurInfo.last_name} par ${identity?.fullName}`
            )
          )
          await Promise.allSettled(commentPromises)

          // Create notification
          createNotification({
            profile_id: pubeurInfo.id,
            type: 'assignation',
            title: `Nouveaux contacts assignés`,
            message: `Votre responsable vous a assigné un ou plusieurs contacts : ${listeOfLeads}.`,
            url: `/leads`,
          })
        }

        // Batch create assignation logs (only for actually updated leads),
        // named or not: a lead without a name still has a holder who loses it
        // (docs/project-specs/past-contacts.md)
        const logEntries = []
        validLeadsInfo.forEach(leadInfo => {
          const update = !(isMomentary && pubeurInfo.id === leadInfo.assigned_pubeur)

          if (update) {
            logEntries.push({
              author: identity?.id,
              pubeur_id: pubeurInfo.id,
              lead_id: leadInfo.id,
              temporary: isMomentary,
              lead_previous_pubeur_id: leadInfo.assigned_pubeur ?? null,
              duration: isMomentary ? dateOfMomentaryAssignation + 'T23:59:59.000' : null,
              current_year_id: process.env.REACT_APP_CURRENT_YEAR_ID
            })
          }
        })

        if (logEntries.length > 0) {
          await logAssignation(logEntries)
        }
      } else {
        // Unassignment logic
        const logEntries = []

        leadsInfo.forEach(leadInfo => {
          logEntries.push({
            author: identity?.id,
            pubeur_id: null,
            lead_id: leadInfo.id,
            temporary: false,
            lead_previous_pubeur_id: leadInfo.assigned_pubeur ?? null,
            duration: null,
            current_year_id: process.env.REACT_APP_CURRENT_YEAR_ID
          })
        })

        if (logEntries.length > 0) {
          await logAssignation(logEntries)
        }

        // Batch create unassignment comments
        const commentPromises = finalLeadIds.map(leadId =>
          addNewLeadComment(leadId, identity, null, `*AUTO* ⚠️ Pubeur assigné retiré par ${identity?.fullName}`)
        )
        await Promise.allSettled(commentPromises)
      }

      // Success notifications and cleanup
      if (!leadIdsProp) {
        unselectAll()
      }
      notify(
        pubeurInfo?.id
          ? isMomentary
            ? `Assignation ponctuelle effectuée avec succès (${updatedCount} contact${updatedCount > 1 ? 's' : ''})`
            : `Assignation effectuée avec succès (${updatedCount} contact${updatedCount > 1 ? 's' : ''})`
          : `Désassignation effectuée avec succès (${updatedCount} contact${updatedCount > 1 ? 's' : ''})`,
        { type: 'success' }
      )
      refresh()
      handleClose()

    } catch (error) {
      console.error('Error in performAssignation: ', error)
      await supabaseClient
        .from('suivi_bilans')
        .insert({
          comment: `Exception in performAssignation: ${error.message}`,
          values: JSON.stringify({
            chunk_size: chunk.length,
            pubeur_id: pubeurInfo?.id,
            error: error.message
          })
        })
    }
  }

  const handleBulkAction = async (pubeurInfo, isMomentary = false, momentaryDate = null) => {
    if (!pubeurInfo) {
      const confirmed = await confirmationDialog({
        confirmation: `Attention, vous allez désassigner le(s) pubeur(s) de ${leads?.length ?? 'un ou plusieurs'} contacts.`
      })
      if (!confirmed) return
    }

    const chunkSize = 100
    const chunks = []
    for (let i = 0; i < leads?.length; i += chunkSize) {
      chunks.push(leads.slice(i, i + chunkSize))
    }

    for (const chunk of chunks) {
      await performAssignation(chunk, pubeurInfo, isMomentary, momentaryDate)
    }
  }

  return (
    <>
      <CommonDialog
        open={openAssignation}
        handleClose={handleClose}
        title={
          leads?.length > 1
            ? `Gérer le pubeur de ${leads?.length} contacts`
            : `Gérer le pubeur du contact sélectionné`
        }
        size="sm"
        icon={<HowToRegIcon />}
        sx={getDialogSx(isDark)}
        compact
        displayDialogActions={false}
      >
        {rolesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <LeadAssignationContent
            handleBulkAction={handleBulkAction}
            isAuthorized={isAuthorized}
            roleAdmin={roleAdmin}
            isAdmin={isAdmin}
            identity={identity}
            leads={leads}
            currentPubeurId={currentPubeurId}
            currentPubeurName={currentPubeurName}
            leadsWithPubeurCount={leadsWithPubeurCount}
          />
        )}
      </CommonDialog>

      {!defaultOpen && (
        <Button
          label="Pubeurs"
          size="small"
          color="primary"
          onClick={() => setOpenAssignation(true)}
        >
          <HowToRegIcon />
        </Button>
      )}
    </>
  )
}

export default PubeurAssignation
